/** Helpers for the date-only `dueDate` field (stored as an ISO timestamp). */

/** Format a due-date ISO string as e.g. "Aug 1" (or "Aug 1, 2027" if not this year). */
export function formatDueShort(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** A compact relative time like "just now", "5m", "3h", "2d", else a date. */
export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** True if the due date is strictly before today (local calendar day). */
export function isOverdue(iso: string): boolean {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/** True if the due date's local calendar day is today or tomorrow. */
export function isDueSoon(iso: string): boolean {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((d.getTime() - today.getTime()) / dayMs);
  return diffDays === 0 || diffDays === 1;
}

/**
 * Convert a stored due-date ISO string to a `yyyy-mm-dd` value for a
 * `<input type="date">`, using the date's *local* calendar day so the value
 * shown matches what the user picked.
 */
export function toDateInputValue(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Convert a `yyyy-mm-dd` value from a date input into an ISO timestamp anchored
 * at local noon, so the calendar day round-trips regardless of timezone.
 */
export function fromDateInputValue(value: string): string {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}
